import { useMemo } from 'react';

import { LoyaltyCmsType, useGetLoyaltyQuestsQuery } from 'generated/graphql-gateway';
import logger from 'utils/logger';

import { createQuest, validateQuest } from './create-quest';
import { IQuest, IUseLoyaltyQuestQuery } from './types';

export const useLoyaltyQuestQuery = ({
  loyaltyId,
  cmsType,
}: {
  loyaltyId: string;
  cmsType?: LoyaltyCmsType;
}): IUseLoyaltyQuestQuery => {
  const { loading, data, error, refetch } = useGetLoyaltyQuestsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      loyaltyId,
      where: {
        activeOrEligible: true,
      },
    },
    skip: !loyaltyId,
  });

  const loyaltyUserQuests = data?.loyaltyUserV2?.quests;
  const quest = useMemo(() => {
    if (!loyaltyUserQuests) {
      return null;
    }

    // create and validate quests
    // invalid quests should be filtered out
    const questList = loyaltyUserQuests.map(q => {
      const createdQuest = createQuest(q);
      const questErrors = validateQuest(createdQuest);

      if (questErrors.length) {
        logger.warn(`Quest validation ${JSON.stringify(questErrors)}`);
        return null;
      }

      return createdQuest;
    });

    // filter invalid quests
    return questList.find(
      (quest: IQuest | null): quest is IQuest =>
        quest !== null &&
        (cmsType
          ? quest.loyaltyCmsType === cmsType
          : quest.loyaltyCmsType !== LoyaltyCmsType.USER_JOURNEY)
    );
    // stringifying the response to create the quest list only if the response has changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(loyaltyUserQuests)]);

  return {
    loadingQuests: loading,
    quest,
    error,
    refetchQuests: refetch,
  };
};
